import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, switchMap, catchError, of, timer, merge, debounceTime, Subject, takeUntil } from 'rxjs';
import { NotificationsCounterApiModel } from '@app/api/api-models';
import { NavNotificationsService } from '@app/api/api-services/nav-notifications.service';
import { TokenStorageService } from '@app/shared/services/token-storage.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationCounterService implements OnDestroy {
  private readonly _refreshNotificationsPeriod = 60000;
  private readonly _destroy$ = new Subject<void>();
  private _isPolling = false;
  private readonly _countersSubject = new BehaviorSubject<NotificationsCounterApiModel>(
    new NotificationsCounterApiModel()
  );
  public counters$ = this._countersSubject.asObservable();

  constructor(
    private readonly notificationsService: NavNotificationsService,
    private readonly tokenStorageService: TokenStorageService
  ) {}

  public startRefreshingNotifications(): void {
    if (this._isPolling) {
      return;
    }

    this._destroy$.next();
    this._isPolling = true;

    const refreshTrigger$ = merge(
      timer(0, this._refreshNotificationsPeriod),
      this.tokenStorageService.userInfoChanged.pipe(debounceTime(100))
    );

    refreshTrigger$
      .pipe(
        takeUntil(this._destroy$),
        switchMap(() => {
          if (!this.tokenStorageService.userInfo?.CurrentProgramId) {
            return of(null);
          }
          return this.notificationsService.getCounters().pipe(
            catchError(() => {
              return of(null);
            })
          );
        })
      )
      .subscribe((counters: NotificationsCounterApiModel | null) => {
        if (counters) {
          this._countersSubject.next(counters);
          this.notificationsService.emitCounterObserver(counters.TotalCount);
        }
      });
  }

  public stopRefreshingNotifications(): void {
    this._isPolling = false;
    this._destroy$.next();
  }

  public handleNotificationStateAction(model: any): void {
    const counters = this._countersSubject.value;
    if (model.isAllDeleted) {
      counters.TotalCount = 0;
      counters.UnreadCount = 0;
      this._countersSubject.next(counters);
      this.notificationsService.emitCounterObserver(counters.TotalCount);
      return;
    }
    if (model.isAllRead) {
      counters.UnreadCount = 0;
      this._countersSubject.next(counters);
      return;
    }
    if (model.isDeleted) {
      counters.TotalCount--;
      counters.UnreadCount = counters.UnreadCount ? counters.UnreadCount - 1 : 0;
      this._countersSubject.next(counters);
      this.notificationsService.emitCounterObserver(counters.TotalCount);
      return;
    }
    if (!model.isRead) {
      counters.UnreadCount = counters.UnreadCount ? counters.UnreadCount - 1 : 0;
      this._countersSubject.next(counters);
    }
  }

  ngOnDestroy(): void {
    this.stopRefreshingNotifications();
  }
}
